// Shared assembly-point list editor — name + directions + photo, repeatable.
// Used by: the site-manager induction wizard, the Inductions "Site content" tab,
// and the project-level "Issue a site induction" panel. Photo uploads happen
// immediately (same pattern as the existing site rule blocks); heading/body/order
// is persisted by the parent's own Save action.
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Camera, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function AssemblyPointsEditor({ siteId, points, setPoints, brand = "var(--brand-accent, #C75A1B)", testidPrefix = "assembly" }) {
    const [uploading, setUploading] = useState(null);
    const add = () => setPoints((p) => [...p, { id: `b${Date.now()}`, heading: "", body: "" }]);
    const update = (id, patch) => setPoints((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    const remove = (id) => setPoints((p) => p.filter((x) => x.id !== id));

    async function uploadPhoto(id, file) {
        setUploading(id);
        const fd = new FormData(); fd.append("file", file);
        try {
            const r = await api.post(`/inductions/sites/${siteId}/assembly-points/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            update(id, { image_path: r.data.image_path });
            toast.success("Photo added");
        } catch { toast.error("Upload failed"); }
        setUploading(null);
    }

    return (
        <div className="space-y-3" data-testid={`${testidPrefix}-list`}>
            {points.map((p, i) => (
                <div key={p.id} data-testid={`${testidPrefix}-row-${i}`} className="border rounded-xl p-3" style={{ borderColor: "#E5E0DA" }}>
                    <div className="flex items-center gap-2 mb-2">
                        <input data-testid={`${testidPrefix}-name-${i}`} value={p.heading} onChange={(e) => update(p.id, { heading: e.target.value })}
                            placeholder="e.g. Main car park assembly point" className="flex-1 h-9 px-2.5 rounded-lg border text-[13px] font-semibold" style={{ borderColor: "#E5E0DA" }} />
                        <button data-testid={`${testidPrefix}-remove-${i}`} onClick={() => remove(p.id)} className="text-red-500"><Trash2 size={15} /></button>
                    </div>
                    <textarea data-testid={`${testidPrefix}-directions-${i}`} value={p.body || ""} onChange={(e) => update(p.id, { body: e.target.value })} rows={2}
                        placeholder="Directions — how to get there from the site entrance" className="w-full px-2.5 py-1.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                    <div className="flex items-center gap-3 mt-2">
                        {p.image_path && <span className="text-[11px] text-green-600 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Photo attached</span>}
                        <label className="text-[11.5px] font-semibold cursor-pointer inline-flex items-center gap-1" style={{ color: brand }}>
                            {uploading === p.id ? <Loader2 className="animate-spin" size={13} /> : <Camera size={13} />} {p.image_path ? "Replace photo" : "Add photo"}
                            <input data-testid={`${testidPrefix}-photo-${i}`} type="file" accept="image/*" className="hidden"
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(p.id, f); }} disabled={uploading === p.id} />
                        </label>
                    </div>
                </div>
            ))}
            <button data-testid={`${testidPrefix}-add`} onClick={add} className="text-[12.5px] font-semibold px-3 py-2 rounded-lg border w-full" style={{ borderColor: "#E5E0DA", color: brand }}>+ Add assembly point</button>
        </div>
    );
}
